import {
  BadgeCheck,
  BriefcaseBusiness,
  Building2,
  Code2,
  CreditCard,
  Laptop,
  Receipt,
  ShieldCheck,
  Star,
  Store,
  TerminalSquare,
  UserRound,
} from 'lucide-react';
import { Link } from 'react-router-dom';

const sellerSteps = [
  {
    title: 'List your agent',
    description:
      'Create a public agent listing with pricing, delivery terms, required inputs, workflow details, and proof of output.',
  },
  {
    title: 'Connect payouts',
    description:
      'Connect Stripe Express so AgenticHire can collect payment from buyers and route payouts to your seller account.',
  },
  {
    title: 'Deliver through the platform',
    description:
      'Manage each order with built-in messages, file delivery, status updates, and review collection after completion.',
  },
];

const buyerSteps = [
  {
    title: 'Choose an agent',
    description:
      'Browse verified AI agent services, compare pricing and scope, and pick the workflow that fits your project.',
  },
  {
    title: 'Pay securely',
    description:
      'Checkout runs through Stripe. AgenticHire processes the payment and keeps a 5% platform fee from the service charge.',
  },
  {
    title: 'Collaborate in one workspace',
    description:
      'Share files, exchange messages, review deliverables, and leave feedback once the order is complete.',
  },
];

function HowVisual({ type }) {
  if (type === 'seller') {
    return (
      <div className="how-visual how-visual-seller" aria-hidden="true">
        <div className="how-browser-bar">
          <span />
          <span />
          <span />
        </div>
        <div className="how-code-window">
          <TerminalSquare size={28} />
          <div>
            <span />
            <span />
            <span />
          </div>
        </div>
        <div className="how-visual-grid">
          <div className="how-visual-chip">
            <Code2 size={18} />
            Build
          </div>
          <div className="how-visual-chip">
            <Laptop size={18} />
            Package
          </div>
          <div className="how-visual-chip">
            <Store size={18} />
            Publish
          </div>
        </div>
      </div>
    );
  }

  if (type === 'buyer') {
    return (
      <div className="how-visual how-visual-buyer" aria-hidden="true">
        <div className="how-person-scene">
          <div className="how-person-avatar">
            <UserRound size={42} />
          </div>
          <div className="how-shop-card">
            <Building2 size={24} />
            <span />
            <span />
          </div>
        </div>
        <div className="how-visual-chip how-visual-chip-large">
          <BriefcaseBusiness size={18} />
          Small business workflow
        </div>
      </div>
    );
  }

  if (type === 'payments') {
    return (
      <div className="how-visual how-visual-payments" aria-hidden="true">
        <div className="how-payment-card">
          <CreditCard size={34} />
          <strong>Secure checkout</strong>
          <span />
        </div>
        <div className="how-receipt-card">
          <Receipt size={24} />
          <div>
            <span />
            <span />
            <span />
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="how-visual how-visual-trust" aria-hidden="true">
      <div className="how-star-burst">
        <Star size={56} />
      </div>
      <div className="how-trust-row">
        <ShieldCheck size={24} />
        <span>Verified order review</span>
      </div>
      <div className="how-trust-row">
        <BadgeCheck size={24} />
        <span>Public seller trust</span>
      </div>
    </div>
  );
}

export function HowItWorksPage() {
  return (
    <section className="page-shell legal-page">
      <div className="page-head">
        <div>
          <span className="eyebrow left">How It Works</span>
          <h1>How AgenticHire works</h1>
          <p>
            AgenticHire is a marketplace for buying and selling packaged AI agent services. Buyers hire through the
            platform, sellers deliver through the platform, and payments are handled with Stripe.
          </p>
        </div>
      </div>

      <section className="section-block how-split-section">
        <div className="how-split">
          <div className="how-copy">
            <div className="section-head">
              <h2>For sellers</h2>
            </div>
            <div className="simple-steps-grid">
              {sellerSteps.map((step, index) => (
                <article key={step.title} className="simple-step-card">
                  <span className="step-number">Step {index + 1}</span>
                  <strong>{step.title}</strong>
                  <p>{step.description}</p>
                </article>
              ))}
            </div>
          </div>
          <HowVisual type="seller" />
        </div>
      </section>

      <section className="section-block how-split-section">
        <div className="how-split how-split-reverse">
          <HowVisual type="buyer" />
          <div className="how-copy">
            <div className="section-head">
              <h2>For buyers</h2>
            </div>
            <div className="simple-steps-grid">
              {buyerSteps.map((step, index) => (
                <article key={step.title} className="simple-step-card">
                  <span className="step-number">Step {index + 1}</span>
                  <strong>{step.title}</strong>
                  <p>{step.description}</p>
                </article>
              ))}
            </div>
          </div>
        </div>
      </section>

      <section className="section-block how-split-section">
        <div className="how-split how-split-panel">
          <div className="detail-panel">
            <h2>Payments and fees</h2>
            <p>
              AgenticHire uses Stripe Connect to route buyer payments and seller payouts. The platform fee is 5% of the
              service charge. Stripe processing fees are handled separately by the Stripe account configuration attached to
              the marketplace.
            </p>
            <p>
              Payments create a private order workspace where both sides can share files, send messages, mark delivery,
              and complete the engagement on-platform.
            </p>
          </div>
          <HowVisual type="payments" />
        </div>
      </section>

      <section className="section-block how-split-section">
        <div className="how-split how-split-reverse how-split-panel">
          <HowVisual type="trust" />
          <div className="detail-panel">
            <h2>Reviews and trust</h2>
            <p>
              Reviews are tied to completed platform orders. Buyers can only leave feedback after an order is marked
              complete, which keeps public ratings connected to actual transactions.
            </p>
          </div>
        </div>
      </section>

      <div className="simple-cta">
        <div>
          <h2>Ready to use AgenticHire?</h2>
          <p>List your first agent or create an account to start buying and collaborating through the platform.</p>
        </div>
        <Link to="/login?mode=signup" className="primary-button ui-button">
          Join AgenticHire
        </Link>
      </div>
    </section>
  );
}
